import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Clapperboard, Download, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u ? `${API}${u}` : '');
const mins = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.round((s || 0) % 60)).padStart(2, '0')}`;

/** Public, no-login watch page for a shared Final Cut. */
export default function WatchPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    axios.get(`${API}/api/public/watch/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'This link could not be opened'));
  }, [token]);
  return (
    <div className="min-h-screen bg-[#0b0b0d] text-neutral-100" data-testid="watch-page">
      <div className="mx-auto max-w-5xl px-5 py-10 sm:py-14">
        <div className="mb-6 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-300">
          <Clapperboard size={14} /> Private screening · Konnect Studio
        </div>
        {!data && !error && <div className="flex items-center gap-2 text-sm text-neutral-400" data-testid="watch-loading"><Loader2 size={16} className="animate-spin" /> Loading…</div>}
        {error && (
          <div className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-8" data-testid="watch-error">
            <div className="text-xl font-semibold">This link is not available</div>
            <div className="mt-2 text-sm text-neutral-400">{error}</div>
          </div>
        )}
        {data && (
          <div className="space-y-5 animate-in fade-in duration-500">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl" data-testid="watch-title">{data.title}</h1>
              <div className="mt-2 text-sm text-neutral-400" data-testid="watch-meta">
                {data.folder}{data.version > 1 ? ` · v${data.version}` : ''} · {mins(data.duration_seconds)}{data.resolution ? ` · ${data.resolution}` : ''}{data.render_style ? ` · ${data.render_style}` : ''}
              </div>
            </div>
            <video src={abs(data.video_url)} poster={abs(data.poster_url)} controls playsInline preload="metadata"
              className="w-full rounded-2xl border border-neutral-800 bg-black shadow-2xl" data-testid="watch-player" />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-xs text-neutral-500" data-testid="watch-expiry">
                {data.never_expires ? 'Available while the owner keeps it in their library' : `Link available until ${new Date(data.effective_until).toLocaleString()}`}
              </span>
              <a href={abs(data.download_url)} className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2 text-sm font-semibold text-neutral-900 transition-colors duration-200 hover:bg-amber-400" data-testid="watch-download-btn">
                <Download size={15} /> Download MP4
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
